import React, { useEffect, useState, useCallback } from 'react'
import { DateTime } from 'luxon'

export interface StandardFooterProps {
  footerImageSrc: string
  absolute?: boolean
  scrollContainer?: React.RefObject<HTMLElement>
}

const FOOTER_CLASS = 'bg-secondary flex flex-col lg:flex-row lg:h-20'
const FOOTER_CONTENT_CLASS =
  'flex h-full bg-secondary flex-col flex-grow-0 flex-shrink-0 w-screen px-4 py-2 md:flex-row'
const FOOTER_CONTENT_ABS_CLASS = `${FOOTER_CONTENT_CLASS} transform transition-transform duration-200 ease-out md:overflow-y md:fixed md:left-0 md:bottom-0 md:z-30 md:h-20`
const FOOTER_TEXT_CLASS = 'font-body text-white my-auto text-xs md:mr-8'
const FOOTER_LINK_CLASS = 'text-white underline'
const FOOTER_OPTION_CLASS = 'px-1 py-2 inline'
const FOOTER_ICON_LINK_CLASS = 'flex h-auto mt-2 md:my-auto md:ml-auto'
const FOOTER_ICON_CLASS = 'flex mr-auto lg:m-auto h-auto w-40'

export const Standard: React.FC<StandardFooterProps> = ({
  footerImageSrc,
  absolute,
  scrollContainer,
}) => {
  const [hidden, setHidden] = useState(Boolean(absolute))

  const updateVisibility = useCallback(() => {
    if (!absolute || typeof window === 'undefined') {
      setHidden(false)
      return
    }

    const scrollElement = scrollContainer?.current
    const isWindow = !scrollElement

    let scrollY: number
    let containerHeight: number
    let contentHeight: number

    if (isWindow) {
      scrollY = window.scrollY
      containerHeight = window.innerHeight
      contentHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight
      )
    } else {
      scrollY = scrollElement.scrollTop
      containerHeight = scrollElement.clientHeight
      contentHeight = scrollElement.scrollHeight
    }

    const hasScrollableContent = contentHeight > containerHeight + 1
    const isAtBottom = containerHeight + scrollY >= contentHeight - 1

    setHidden(hasScrollableContent && !isAtBottom)
  }, [absolute, scrollContainer])

  useEffect(() => {
    if (!absolute || typeof window === 'undefined') {
      setHidden(false)
      return
    }

    const scrollElement = scrollContainer?.current || window
    const isWindow = scrollElement === window

    updateVisibility()

    if (isWindow) {
      window.addEventListener('scroll', updateVisibility)
      window.addEventListener('resize', updateVisibility)
      return () => {
        window.removeEventListener('scroll', updateVisibility)
        window.removeEventListener('resize', updateVisibility)
      }
    } else {
      scrollElement.addEventListener('scroll', updateVisibility)
      window.addEventListener('resize', updateVisibility)
      return () => {
        scrollElement.removeEventListener('scroll', updateVisibility)
        window.removeEventListener('resize', updateVisibility)
      }
    }
  }, [absolute, scrollContainer, updateVisibility])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const frame = window.requestAnimationFrame(updateVisibility)
    return () => window.cancelAnimationFrame(frame)
  })

  const year = DateTime.local().toFormat('yyyy')

  return (
    <footer className={FOOTER_CLASS}>
      <div
        className={`${
          absolute ? FOOTER_CONTENT_ABS_CLASS : FOOTER_CONTENT_CLASS
        }`}
        style={
          absolute
            ? { transform: hidden ? 'translateY(100%)' : 'translateY(0)' }
            : undefined
        }
      >
        <p className={FOOTER_TEXT_CLASS}>
          Copyright {year}{' '}
          <a
            className={FOOTER_LINK_CLASS}
            href="https://www.maricopa.gov"
          >
            Maricopa County
          </a>
          . All rights reserved.
          <span className={FOOTER_OPTION_CLASS}>
            <a
              className={FOOTER_LINK_CLASS}
              href="https://www.maricopa.gov/124/Site-Terms-Privacy-Policy"
            >
              Privacy Policy
            </a>
          </span>
          <span className={FOOTER_OPTION_CLASS}>
            <a
              className={FOOTER_LINK_CLASS}
              href="https://www.maricopa.gov/124/Site-Terms-Privacy-Policy"
            >
              Terms of Use
            </a>
          </span>
        </p>
        <a
          href="https://www.maricopa.gov"
          className={FOOTER_ICON_LINK_CLASS}
        >
          <img
            src={footerImageSrc}
            className={FOOTER_ICON_CLASS}
            alt="Maricopa County"
          />
        </a>
      </div>
    </footer>
  )
}
